import * as React from 'react';
import { useParams } from 'react-router-dom';
import { user as stardogUser } from 'stardog';
import { getStardogConnection } from 'vet-bones/utils';

import { Connection } from 'src/ui/graph/types';
import { runWithRefetchConnectionToken } from 'src/ui/hooks/refetchConnectionToken';

export const useServerRoles = (connections: Connection[], enabled: boolean) => {
  const { org = '' } = useParams<{ org?: string }>();
  const [connectionRolesById, setConnectionRolesById] = React.useState<
    Record<string, { isLoading: boolean; roles: Set<string> | null }>
  >({});

  React.useEffect(() => {
    let isCurrent = true;

    setConnectionRolesById((prev) =>
      connections.reduce((acc, connection) => {
        const existing = acc[connection.id];
        if (existing?.roles) {
          return acc;
        }
        return {
          ...acc,
          [connection.id]: { isLoading: enabled, roles: null },
        };
      }, prev)
    );

    if (enabled) {
      Promise.all(
        connections.map(async (connection) => {
          const stardogConnection = getStardogConnection(
            {
              endpoint: connection.endpoint,
              username: connection.username || undefined,
              token: connection.token || undefined,
            },
            { agent: 'Portal' }
          );

          try {
            const response = await runWithRefetchConnectionToken(
              org
            )(stardogConnection, connection.index, (stardogConn) =>
              stardogUser.role.list(stardogConn)
            );

            if (!isCurrent) {
              return;
            }

            if (!response.ok) {
              throw new Error(response.statusText || String(response.status));
            }

            setConnectionRolesById((prev) => ({
              ...prev,
              [connection.id]: {
                isLoading: false,
                roles: new Set(response.body?.roles || []),
              },
            }));
          } catch (err) {
            if (!isCurrent) {
              return;
            }

            console.error(`Failed to fetch roles: ${err}`);
            setConnectionRolesById((prev) => ({
              ...prev,
              [connection.id]: { isLoading: false, roles: null },
            }));
          }
        })
      );
    }

    return () => {
      isCurrent = false;
    };
  }, [connections, enabled, org]);

  return connectionRolesById;
};
